import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { Box, DialogActions, Divider, IconButton, LinearProgress } from '@mui/material';
import InventoryIcon from '@mui/icons-material/Inventory';

import useNotification from '../../../../core/hooks/use_notification';
import { Button } from '../../../common/button';
import { getAllProductProductsNotificationData, updateAdvertisementProductsNotificationData } from '../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../core/enums/common/api';
import { updateAdvertisementProductsAction, updateAdvertisementProductsState } from "../../../../features/pod/update_advertisement_products";
import { getAllProductsAction, getAllProductsState } from "../../../../features/product/get_all_products";
import _ from "lodash";
import AutocompleteMultipleSearchOnType from "../../../common/autocomplete";

const AdvertisementProductDialog = ({ advertisement, refreshList }) => {
    const { showNotification, showErrorMessage } = useNotification();
    const dispatch = useDispatch();

    const updateAdvertisementProducts = useSelector(updateAdvertisementProductsState);
    const getAllProducts = useSelector(getAllProductsState);

    const mapProductsOptions = useCallback((data) => {
        return data?.map(product => ({
            key: product?.id + Math.random(),
            value: product?.id,
            label: product?.name
        }));
    }, []);

    const [open, setOpen] = useState(false);
    const [productsOptions, setProductsOptions] = useState(mapProductsOptions(getAllProducts?.data) || []);
    const [loadingProductsOptions, setLoadingProductsOptions] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false);
    const [chosenProducts, setChosenProducts] = useState([]);

    const onOpen = () => {
        setInitialLoading(true);
        dispatch(getAllProductsAction())
            .then(response => {
                showErrorMessage(getAllProductProductsNotificationData(response));
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    const initialProducts = mapProductsOptions(response?.payload.filter(product => advertisement?.productIds?.includes(product?.id))) || [];
                    setChosenProducts(initialProducts);
                    setProductsOptions(initialProducts);
                }
                setInitialLoading(false);
            });
        setOpen(true);
    }

    const onClose = () => setOpen(false);

    const onProductsInputChange = (query) => {
        if (query.length >= 3) {
            setProductsOptions([]);
            setLoadingProductsOptions(true);
            getProductsDebounced(query);
        }
    };

    const getProductsDebounced = _.debounce((query) => {
        dispatch(getAllProductsAction({ query }))
            .then(response => {
                showErrorMessage(getAllProductProductsNotificationData(response));
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    const newProductsOptions = mapProductsOptions(response?.payload).concat(chosenProducts);
                    const newDistinctProductsOptions = [...new Map(newProductsOptions.map((option) => [option['value'], option])).values()];
                    setProductsOptions(newDistinctProductsOptions);
                }
                setLoadingProductsOptions(false);
            });
    }, 600)

    const onSubmit = () => {
        const productIds = chosenProducts?.map(product => product?.value) || [];
        const form = { productIds };

        dispatch(updateAdvertisementProductsAction({ id: advertisement?.id, form }))
            .then(response => {
                showNotification(updateAdvertisementProductsNotificationData(response));
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    refreshList && refreshList();
                    onClose();
                }
            });
    };

    return (
        <>
            <IconButton title="Zmień powiązane produkty" onClick={ onOpen }>
                <InventoryIcon/>
            </IconButton>
            <Dialog open={ open } onClose={ onClose } fullWidth maxWidth="md">
                <DialogTitle sx={ { fontWeight: 600, pb: 1 } }>Zmień powiązane produkty</DialogTitle>
                <Divider/>

                <DialogContent sx={ { py: 3 } }>
                    { initialLoading ? (
                        <Box display="flex" justifyContent="center" my={ 4 }><LinearProgress sx={ { width: '100%' } }/></Box>
                    ) : (
                        <Box>
                            <AutocompleteMultipleSearchOnType
                                size="small"
                                options={ productsOptions }
                                value={ chosenProducts }
                                setValue={ (newValue) => setChosenProducts(newValue) }
                                onInputChange={ (value) => onProductsInputChange(value) }
                                name="products"
                                label="Uzależnij reklamę od występowania wskazanego produktu"
                                loading={ loadingProductsOptions }
                            />
                        </Box>
                    ) }
                </DialogContent>

                <Divider/>
                <DialogActions sx={ { p: 2, px: 3, gap: 1 } }>
                    <Button variant="outlined" onClick={ onClose }>Zamknij</Button>
                    <Button onClick={ onSubmit } isLoading={ updateAdvertisementProducts?.loading }>Zapisz</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AdvertisementProductDialog;